import React, { useState, useEffect } from "react";
import Navbar from "../navbar/Navbar";
import Footer from "../footer/Footer";
import waimg from "../../../assets/images/whatsapp.png";
import { handleAPIRequest } from "../../../api/api";
import { FaSearch } from "react-icons/fa";
import { ToastContainer } from "react-toastify";

const Specifications = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSubSubCategory, setSelectedSubSubCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await handleAPIRequest("get", "ifrc/categories");
        setCategories(response || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const fetchCategoryDetails = async (categoryId) => {
    try {
      const response = await handleAPIRequest(
        "get",
        `ifrc/category/${categoryId}`
      );
      setSelectedCategory(response);
      setSubCategories(response.sub_categories || []);
      setSubSubCategories([]);
      setProducts([]);
      setSelectedSubSubCategory(null);
      setSelectedProduct(null);
    } catch (error) {
      console.error("Error fetching category details:", error);
    }
  };

  const fetchProducts = async (subSubCategoryId) => {
    try {
      const response = await handleAPIRequest(
        "get",
        `ifrc/products/${subSubCategoryId}`
      );
      setProducts(response || []);
      setSelectedSubSubCategory(subSubCategoryId);
      setSelectedProduct(null);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchProductDetails = async (productId) => {
    try {
      const response = await handleAPIRequest(
        "get",
        `ifrc/product/${productId}`
      );
      setSelectedProduct(response);
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };

  // 🔎 Search Logic
  useEffect(() => {
    if (!searchTerm) {
      setFilteredResults([]);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();

    const filteredCategories = categories.filter((category) =>
      category.category_name.toLowerCase().includes(lowerSearch)
    );

    const filteredSubCategories = subCategories.flatMap((subcategory) =>
      subcategory.sub_category_name.toLowerCase().includes(lowerSearch)
        ? [{ ...subcategory, category_id: selectedCategory?.id }]
        : []
    );

    const filteredSubSubCategories = subCategories.flatMap((subcategory) =>
      subcategory.sub_sub_categories
        .filter((subsub) =>
          subsub.sub_sub_category_name.toLowerCase().includes(lowerSearch)
        )
        .map((subsub) => ({ ...subsub, sub_category_id: subcategory.id }))
    );

    setFilteredResults([
      ...filteredCategories,
      ...filteredSubCategories,
      ...filteredSubSubCategories,
    ]);
  }, [searchTerm, categories, subCategories]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 text-black">
      <Navbar />
      <div className="bg-white p-6 text-center">
        <h2 className="text-2xl font-bold mb-2">Item Specifications</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Browse through categories to find detailed product specifications.
        </p>
      </div>

      {/* 🔍 Search Bar */}
      <div className="bg-white shadow-md w-full px-4 py-3 flex items-center relative">
      <div className="flex gap-3 overflow-x-auto w-full pr-12 whitespace-nowrap">

          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => fetchCategoryDetails(category.id)}
              className={`px-4 py-2 border rounded font-medium transition-all ${
                selectedCategory?.id === category.id
                  ? "bg-blue-500 text-white"
                  : "bg-gray-300"
              }`}
            >
              {category.category_name}
            </button>
          ))}
        </div>
        <div className="absolute right-4">
          <FaSearch
            className="text-gray-600 cursor-pointer text-xl"
            onClick={() => setShowSearch(!showSearch)}
          />
        </div>
      </div>

      {/* 🔍 Search Input */}
      {showSearch && (
        <div className="p-4">
          <input
            type="text"
            placeholder="Search categories, subcategories..."
            className="w-full p-2 sm:p-3 border rounded text-sm sm:text-base"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* 🔎 Search Results */}
      {searchTerm && filteredResults.length > 0 && (
        <div className="p-4">
          <h3 className="text-lg font-bold mb-2">Search Results:</h3>
          <ul className="bg-white shadow-md rounded p-3">
            {filteredResults.map((item, index) => (
              <li
                key={index}
                className="p-2 border-b cursor-pointer hover:bg-gray-200"
                onClick={() => {
                  if (item.category_name) {
                    fetchCategoryDetails(item.id);
                  } else if (item.sub_category_name) {
                    fetchCategoryDetails(item.category_id);
                  } else if (item.sub_sub_category_name) {
                    fetchProducts(item.id);
                  }
                  setSearchTerm(""); // Clear search after selection
                  setShowSearch(false);
                }}
              >
                {item.category_name ||
                  item.sub_category_name ||
                  item.sub_sub_category_name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex-grow p-6 max-w-7xl mx-auto">
        {/* 🔹 Show Subcategories */}
        {!selectedSubSubCategory && !selectedProduct && selectedCategory && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {subCategories.map((subcategory) => (
              <div
                key={subcategory.id}
                className="p-4 border rounded bg-white shadow-md"
              >
                <h4 className="font-bold text-lg mb-2">
                  {subcategory.sub_category_name}
                </h4>
                <ul className="list-disc pl-5 text-gray-700">
                  {subcategory.sub_sub_categories.map((subsub) => (
                    <li
                      key={subsub.id}
                      className="py-1 cursor-pointer hover:text-blue-500 transition"
                      onClick={() => fetchProducts(subsub.id)}
                    >
                      {subsub.sub_sub_category_name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* 🔹 Show Products */}
        {selectedSubSubCategory && !selectedProduct && (
          <>
            <h3 className="text-xl font-bold mb-4">Products</h3>
            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="p-3 border rounded bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    onClick={() => fetchProductDetails(product.id)}
                  >
                    {product.product_name}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center">No products available</p>
            )}
          </>
        )}
      </div>
      {selectedProduct && (
  <div className="p-4 sm:p-6 bg-white shadow-md rounded-lg max-w-3xl w-full mx-auto mb-5">
    <button 
      className="mb-4 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
      onClick={() => setSelectedProduct(null)} // Go back to product list
    >
      ← Back to Products
    </button>
    
    <h2 className="text-2xl font-bold mb-3">{selectedProduct.product_name}</h2>
    
    <div className="border-t pt-4">
      {/* Render Product Description as HTML */}
      <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: selectedProduct.description }} />
    </div>
  </div>
)}

      <Footer />
    </div>
  );
};

export default Specifications;
